import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router'
import { Search, Users, ArrowRightLeft, Phone, FileText, Sparkles } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth'
import { formatPhone } from '@/lib/phone'
import { labelForType } from '@/types/contacts'
import { cn } from '@/lib/utils'

interface SearchResult {
  id: string
  type: 'contact' | 'transaction' | 'call' | 'document'
  title: string
  subtitle: string
  link: string
}

export function CommandPalette() {
  const { activeOrg } = useAuth()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<SearchResult[]>([])
  const [loading, setLoading] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const previousFocusRef = useRef<HTMLElement | null>(null)

  const closeAndRestoreFocus = useCallback(() => {
    setOpen(false)
    previousFocusRef.current?.focus()
  }, [])

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setOpen(prev => {
          if (!prev) {
            previousFocusRef.current = document.activeElement as HTMLElement
          }
          return !prev
        })
      }
      if (e.key === 'Escape') closeAndRestoreFocus()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [closeAndRestoreFocus])

  // Reset palette state during render on the closed→open transition (the
  // React-documented "adjust state when a prop changes" pattern) rather than
  // synchronously inside an effect (react-hooks/set-state-in-effect).
  const [prevOpen, setPrevOpen] = useState(open)
  if (prevOpen !== open) {
    setPrevOpen(open)
    if (open) { setQuery(''); setResults([]); setSelectedIndex(0) }
  }
  // Clear stale results during render whenever there is nothing to search.
  if ((!query.trim() || !activeOrg) && results.length > 0) {
    setResults([])
  }
  // Loading ownership (same render-phase adjust pattern). ON: the moment the
  // query changes to something searchable — not the debounce fire — so a
  // retype while a run is in flight never falls through to a false
  // "No results" in the cancellation→new-debounce window (the search effect's
  // old unconditional setLoading(false) cleanup flashed exactly that, plus its
  // live-region announcement, from the first continuation keystroke until the
  // new run resolved). OFF: only the live run's settle path, or here when
  // there is nothing to search and no run will start to clear it. The search
  // effect's cleanup never touches loading.
  const [prevQuery, setPrevQuery] = useState(query)
  if (prevQuery !== query) {
    setPrevQuery(query)
    if (query.trim() && activeOrg) setLoading(true)
  }
  if ((!query.trim() || !activeOrg) && loading) {
    setLoading(false)
  }

  useEffect(() => {
    if (!open) return
    const focusTimeout = setTimeout(() => inputRef.current?.focus(), 50)
    return () => clearTimeout(focusTimeout)
  }, [open])

  // Monotonic request id (the ContactPicker #192 idiom): the four entity
  // queries resolve out of order and a superseded run must never render — the
  // old awaited chain had a live stale-clobber race where an abandoned slow
  // run's final setResults overwrote a newer run's rows.
  const searchSeq = useRef(0)

  useEffect(() => {
    if (!query.trim() || !activeOrg) return

    // Closure-scoped abandonment flag: cleanup (query changed/cleared,
    // unmount) kills THIS run without touching the ref (the exhaustive-deps
    // ref-in-cleanup heuristic), while searchSeq keeps cross-run response
    // ordering airtight.
    let cancelled = false

    const timeout = setTimeout(() => {
      const seq = ++searchSeq.current
      // Normally already true via the render-phase adjust above; kept for a
      // run not preceded by a query change (e.g. activeOrg arriving late).
      setLoading(true)
      setResults([])
      setSelectedIndex(0)
      const orgId = activeOrg.organization_id
      const q = query.trim()

      // The four entity queries run in PARALLEL and each section renders as it
      // resolves (s8-search-relationships-01) — fast contact hits are no longer
      // gated behind the slow call_logs FTS. Sections land in fixed slots
      // flattened in canonical order (contact → transaction → call → document),
      // so a late-arriving section never reorders rows already on screen.
      const slots: Record<SearchResult['type'], SearchResult[]> = {
        contact: [], transaction: [], call: [], document: [],
      }
      const merge = (type: SearchResult['type'], rows: SearchResult[]) => {
        // an abandoned or superseded run must never render
        if (cancelled || seq !== searchSeq.current) return
        slots[type] = rows
        setResults([...slots.contact, ...slots.transaction, ...slots.call, ...slots.document])
      }

      // Each section uses two-arg .then(onOk, onErr): a query error still
      // resolves { data: null } (that section stays silently empty — existing
      // behavior), but a network-level REJECTION used to throw out of the
      // awaited chain and wedge the palette in "Searching..." forever.
      // Rejection → empty merge, so the settle below always runs.

      // Universal search RPC (mig 121): matches name/email/phone plus addresses,
      // tags, and lead source; match_context explains a non-obvious hit.
      const contactsQ = supabase.rpc('search_contacts', {
        p_org_id: orgId, p_query: q, p_limit: 5, p_offset: 0,
      }).then(({ data }) => {
        merge('contact', (data ?? []).map(hit => {
          const co = hit.contact
          return {
            id: co.id, type: 'contact' as const,
            title: [co.first_name, co.last_name].filter(Boolean).join(' ') || 'Unnamed',
            // Final fallback is the humanized type label, never the raw enum
            // token ("past_client") — s8-search-relationships-04. Intentional
            // || fallthrough: empty strings must fall to the next candidate.
            subtitle: hit.match_context || co.email || formatPhone(co.phone) || labelForType(co.type),
            link: '/contacts/' + co.id,
          }
        }))
      }, () => merge('contact', []))

      const txQ = supabase
        .from('transactions').select('id, property_address, status')
        .eq('organization_id', orgId)
        .ilike('property_address', '%' + q + '%').limit(5)
        .then(({ data }) => {
          merge('transaction', (data ?? []).map(tx => ({
            id: tx.id, type: 'transaction' as const, title: tx.property_address,
            subtitle: tx.status?.replace('_', ' ') || '', link: '/transactions/' + tx.id,
          })))
        }, () => merge('transaction', []))

      const callsQ = supabase
        .from('call_logs').select('id, caller_number, ai_summary')
        .eq('organization_id', orgId)
        // websearch parser: same AND semantics for bare words as the old ' & '
        // join, but never 400s on punctuation ("test!", "St. Anthony") — the
        // strict parser silently emptied this section (s1-mls-stale-01 sweep).
        .textSearch('transcript_text', q, { type: 'websearch', config: 'english' }).limit(3)
        .then(({ data }) => {
          merge('call', (data ?? []).map(call => ({
            id: call.id, type: 'call' as const,
            title: call.ai_summary?.substring(0, 80) || 'Call from ' + call.caller_number,
            subtitle: 'Call transcript', link: '/calls',
          })))
        }, () => merge('call', []))

      const docsQ = supabase
        .from('documents').select('id, name, doc_type, subdivision')
        .eq('organization_id', orgId)
        .ilike('name', '%' + q + '%').limit(3)
        .then(({ data }) => {
          merge('document', (data ?? []).map(doc => ({
            id: doc.id, type: 'document' as const, title: doc.name,
            subtitle: [doc.doc_type, doc.subdivision].filter(Boolean).join(' - '), link: '/documents',
          })))
        }, () => merge('document', []))

      void Promise.all([contactsQ, txQ, callsQ, docsQ]).then(() => {
        // Only the live run may clear the spinner (an old run finishing while a
        // newer one is in flight must not flash "No results").
        if (!cancelled && seq === searchSeq.current) setLoading(false)
      })
    }, 200)

    // Abandoned runs (query changed/cleared, unmount) must never render:
    // cancelled orphans this run's in-flight responses. Loading is
    // deliberately NOT cleared here — on a retype the query is still
    // non-empty and the spinner must survive the cancellation→new-debounce
    // window; the nothing-to-search path is owned by the render-phase
    // adjustment above, and the live run's settle path owns the rest.
    // (StrictMode dev double-invoke is idempotent.)
    return () => { cancelled = true; clearTimeout(timeout) }
  }, [query, activeOrg])

  function handleSelect(result: SearchResult) { closeAndRestoreFocus(); navigate(result.link) }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') { e.preventDefault(); setSelectedIndex(prev => Math.min(prev + 1, results.length - 1)) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setSelectedIndex(prev => Math.max(prev - 1, 0)) }
    else if (e.key === 'Enter' && results[selectedIndex]) { handleSelect(results[selectedIndex]) }
  }

  const icons: Record<string, React.ElementType> = {
    contact: Users, transaction: ArrowRightLeft, call: Phone, document: FileText,
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-[60]" role="dialog" aria-modal="true" aria-label="Global search">
      <div className="absolute inset-0 bg-black/60" onClick={closeAndRestoreFocus} aria-hidden="true" />
      <div className="relative mx-auto mt-[15vh] w-full max-w-lg">
        <div className="rounded-lg border border-border bg-card shadow-2xl">
          <div className="flex items-center gap-3 border-b border-border px-4 py-3">
            <Search className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            <input
              ref={inputRef}
              type="search"
              role="combobox"
              aria-label="Search contacts, transactions, calls, documents"
              aria-autocomplete="list"
              aria-controls="command-palette-results"
              aria-activedescendant={results[selectedIndex] ? 'cmd-result-' + selectedIndex : undefined}
              aria-expanded={results.length > 0 || loading}
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search contacts, transactions, calls, documents..."
              className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground focus:outline-none"
            />
            <kbd className="rounded border border-border px-1.5 py-0.5 text-[10px] text-muted-foreground" aria-label="Press Escape to close">ESC</kbd>
          </div>

          {query.trim() && (
            <button
              onClick={() => {
                const q = query.trim()
                closeAndRestoreFocus()
                window.dispatchEvent(new CustomEvent('rize:ask', { detail: q }))
              }}
              className="flex w-full items-center gap-3 border-b border-border px-4 py-2.5 text-left transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
            >
              <Sparkles className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
              <span className="min-w-0 flex-1 truncate text-sm">
                Ask Rize: <span className="text-muted-foreground">“{query.trim()}”</span>
              </span>
              <span className="shrink-0 rounded bg-primary/10 px-1.5 py-0.5 text-[10px] text-primary">AI</span>
            </button>
          )}

          <span role="status" aria-live="polite" aria-atomic="true" className="sr-only">
            {loading ? 'Searching...' : query && results.length === 0 ? 'No results for ' + query : results.length > 0 ? results.length + ' result' + (results.length === 1 ? '' : 's') : ''}
          </span>

          {/* Results win over the spinner so sections render progressively as
              they resolve; "No results" is only reachable once every section
              has settled (loading false), never mid-flight. */}
          <div id="command-palette-results" className="max-h-80 overflow-y-auto" role="listbox" aria-label="Search results">
            {results.length > 0 ? (
              results.map((result, i) => {
                const Icon = icons[result.type] || FileText
                return (
                  <button
                    id={'cmd-result-' + i}
                    key={result.type + '-' + result.id}
                    role="option"
                    aria-selected={i === selectedIndex}
                    onClick={() => handleSelect(result)}
                    className={cn(
                      'flex w-full items-center gap-3 px-4 py-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
                      i === selectedIndex ? 'bg-primary/10' : 'hover:bg-muted/50'
                    )}
                  >
                    <Icon className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">{result.title}</p>
                      <p className="truncate text-xs text-muted-foreground">{result.subtitle}</p>
                    </div>
                    <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground capitalize">{result.type}</span>
                  </button>
                )
              })
            ) : loading ? (
              <p className="p-4 text-center text-xs text-muted-foreground">Searching...</p>
            ) : query ? (
              <p className="p-4 text-center text-xs text-muted-foreground">No results</p>
            ) : (
              <div className="p-4 text-center text-xs text-muted-foreground">
                Type to search across contacts, transactions, calls, and documents.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

